/* eslint-disable import/no-cycle */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { position } from 'styled-system';
import { Box, Flex, Image } from 'rebass';
import { useLocation } from 'react-router-dom';
import useClickAway from 'react-use/lib/useClickAway';

import { ScrollShadow } from '@lib/scroll-shadow';
import { Hamburger } from '@oca/icons';
import { NavList } from './nav-list';

export const Navigation = React.memo(function Navigation(props) {
  const { routes, open, toggle, onClose } = props;
  const navigationRef = useRef(null);
  const location = useLocation();

  useClickAway(navigationRef, onClose);

  return (
    <NavigationWrapper ref={navigationRef} boxShadow={4} data-is-open={open}>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        pr={3}
        paddingTop={10}
      >
        <Image
          width={140}
          height={80}
          src={open ? '/logo/logo-text.svg' : '/logo/logo.svg'}
        />
        <MenuButton onClick={toggle} />
      </Flex>
      <Container open={open}>
        <ScrollShadow
          topShadowColors={{
            active:
              'linear-gradient(180deg, rgba(0,0,0,0.3) 0%, rgba(255,255,255,0) 70%)',
            inactive: '#F3F4F7',
          }}
          bottomShadowColors={{
            active:
              'linear-gradient(0deg, rgba(0,0,0,0.3) 0%, rgba(255,255,255,0) 70%)',
            inactive: '#F3F4F7',
          }}
          shadowSize={12}
          hideScrollbar
        >
          <NavList
            collapsed={!open}
            depth={0}
            location={location}
            pages={routes}
            onItemClick={onClose}
          />
        </ScrollShadow>
      </Container>
    </NavigationWrapper>
  );
});

Navigation.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      name: PropTypes.string,
      routes: PropTypes.array,
    }),
  ).isRequired,
  // eslint-disable-next-line react/require-default-props
  open: PropTypes.bool,
  // eslint-disable-next-line react/require-default-props
  toggle: PropTypes.func,
  // eslint-disable-next-line react/require-default-props
  onClose: PropTypes.func,
};

const Container = styled(Box)`
  /* Fill the space below the logo and become a flex column so the inner
     ScrollShadow gets a bounded height and can scroll when the expanded
     sections exceed the viewport. min-height: 0 lets it shrink below its
     content (required for overflow scrolling inside a flex parent). */
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  transform: ${({ open }) => (open ? 'translateY(-25px)' : 'none')};
`;

const NavigationWrapper = styled(Box)`
  height: 100%;
  background: #f3f4f7;
  display: flex;
  flex-direction: column;
  transition: width 100ms;
  will-change: width;

  &[data-is-open='true'] {
    width: var(--nav-expanded-width);
  }
  &[data-is-open='false'] {
    width: var(--nav-collapsed-width);
  }
  ${position}
`;

// eslint-disable-next-line react/prop-types
const MenuButton = ({ onClick }) => (
  // eslint-disable-next-line react/button-has-type
  <button
    style={{
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
    }}
    onClick={onClick}
  >
    <Hamburger />
  </button>
);
